import {
  IsBoolean,
  IsInt,
  IsNotEmpty,
  IsString,
  Max,
  Min,
  ValidateIf,
  ValidateNested,
} from '@pristine-ts/class-validator';
import { v4 as uuid } from 'uuid';
import { translationBaseKey } from '../../../decorators/translation-base-key.decorator';
import { FieldTypeEnum } from '../../../enums/field-type.enum';
import { advancedFormControl, FormControlTypeEnum } from '@magieno/common';
import { ErrorMessageBuilder } from '../../../builders/error-message.builder';
import { CalculationDate } from '../../../models/calculation.date';
import { type } from '@pristine-ts/data-mapping-common';

@translationBaseKey('client-questionnaire-2021-quebec-tax-form-child-options')
export class Quebec2022TaxQuestionnaireChildOptions {
  @advancedFormControl({
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Hidden,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  id: string = uuid();

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quel est le nom de votre enfant?',
        en: '',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  childName: string;

  @type(() => new CalculationDate())
  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quelle est la date de naissance de votre enfant?',
        en: '',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.CalculationDate,
    type: FormControlTypeEnum.DatePicker,
  })
  @ValidateNested()
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  childBirthday: CalculationDate;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quel est le numéro d\'assurance social de votre enfant?',
        en: '',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  childSIN: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que votre enfant est en garde partagée?',
        en: '',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  childSharedCustody: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Indiquez des informations supplémentaires sur l’entente de la garde partagée, s\'il y a lieu?',
        en: '',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.String,
    type: FormControlTypeEnum.Input,
  })
  @IsString({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.childSharedCustody === true)
  childSharedCustodyComment: string;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quel est votre pourcentage de garde?',
        en: '',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.Number,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @Min(0, { buildErrorMessage: ErrorMessageBuilder.build })
  @Max(100, { buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.childSharedCustody === true)
  childShareCustodyPercentage: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que l\'enfant effectue des études post-secondaires?',
        en: '',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  childPostSecondaryStudy: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Quel est le revenu gagné de votre enfant?',
        en: '',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.Money,
    type: FormControlTypeEnum.Input,
  })
  @IsInt({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  childEarnedRevenue: number;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des frais de garde pour votre enfant?',
        en: '',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  childAreChildCareExpenses: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des frais de garde en CPE',
        en: '',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.childAreChildCareExpenses === true)
  areChildCPEExpenses: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des frais de garde autres qu\'en CPE',
        en: '',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @ValidateIf(o => o.childAreChildCareExpenses === true)
  mainSpouseIsCareExpensesOtherThanCPE: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Avez-vous payé des frais pour vos enfants relatifs à des activités physiques ou artistiques?',
        en: '',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  areChildArtisticActivity: boolean;

  @advancedFormControl({
    displayableElements: {
      labelTitle: {
        fr: 'Est-ce que votre enfant est handicapé?',
        en: 'Is your child handicaped?',
      },
      tooltip: {
        fr: 'Tooltip',
      },
    },
    fieldType: FieldTypeEnum.Boolean,
    type: FormControlTypeEnum.BooleanSelect,
  })
  @IsNotEmpty({ buildErrorMessage: ErrorMessageBuilder.build })
  @IsBoolean({ buildErrorMessage: ErrorMessageBuilder.build })
  isHandicapped: boolean;

}
